import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import { ArrowLeft, Phone, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function PatientLogin() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState("phone"); // 'phone' | 'otp'
  const [phoneDigits, setPhoneDigits] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [mockMode, setMockMode] = useState(true);

  const fullPhone = `+91${phoneDigits}`;

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/patient/send-otp", { phone: fullPhone });
      setMockMode(!!data.mock_mode);
      toast.success("OTP sent", { description: data.mock_mode ? `Demo mode — any 6 digits will work` : `We sent a code to ${fullPhone}` });
      setStep("otp");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(otp)) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/patient/verify-otp", {
        phone: fullPhone,
        otp,
      });
      setSession(data.access_token, { ...data.user, role: data.role });
      toast.success("Welcome to Healthink");
      navigate("/patient/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg-pattern flex flex-col">
      <header className="px-6 lg:px-10 h-16 flex items-center">
        <Link
          to="/"
          data-testid="patient-login-back-link"
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-border rounded-3xl shadow-sm p-7 sm:p-9">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
            <ShieldCheck className="h-3.5 w-3.5" /> Patient sign-in
          </div>

          {step === "phone" ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                Enter your mobile
              </h1>
              <p className="text-sm text-muted-foreground mb-7">
                We'll send a 6-digit code to verify it's you.
              </p>

              <form onSubmit={sendOtp} className="space-y-5" data-testid="patient-phone-form">
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium mb-2 inline-block">
                    Mobile number
                  </Label>
                  <div className="flex items-stretch border border-border rounded-xl focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all overflow-hidden bg-white">
                    <span className="px-4 inline-flex items-center bg-secondary text-secondary-foreground text-sm font-medium border-r border-border">
                      <Phone className="h-4 w-4 mr-1.5" /> +91
                    </span>
                    <Input
                      id="phone"
                      data-testid="patient-phone-input"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={10}
                      placeholder="9000000001"
                      value={phoneDigits}
                      onChange={(e) =>
                        setPhoneDigits(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      className="h-14 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base rounded-none"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Try{" "}
                    <button
                      type="button"
                      data-testid="patient-prefill-demo-1"
                      onClick={() => setPhoneDigits("9000000001")}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      9000000001
                    </button>{" "}
                    or{" "}
                    <button
                      type="button"
                      data-testid="patient-prefill-demo-2"
                      onClick={() => setPhoneDigits("9000000002")}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      9000000002
                    </button>{" "}
                    for the demo seed patients.
                  </p>
                </div>

                <Button
                  type="submit"
                  data-testid="patient-send-otp-btn"
                  disabled={loading}
                  className="w-full h-14 rounded-full text-base bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                >
                  {loading ? "Sending…" : "Send OTP"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                Enter the code
              </h1>
              <p className="text-sm text-muted-foreground mb-1">
                We sent a 6-digit OTP to{" "}
                <span className="text-foreground font-medium">{fullPhone}</span>
              </p>
              <p className="text-xs text-primary mb-7" data-testid="otp-demo-hint">
                {mockMode ? "Demo mode: enter any 6 digits" : "OTP sent to your phone — valid for 5 minutes"}
              </p>

              <form onSubmit={verifyOtp} className="space-y-6" data-testid="patient-otp-form">
                <div className="flex justify-center" data-testid="patient-otp-input-wrap">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    containerClassName="gap-2 sm:gap-3"
                  >
                    <InputOTPGroup className="gap-2 sm:gap-3">
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          data-testid={`otp-slot-${i}`}
                          className="w-11 h-14 sm:w-12 sm:h-16 text-xl sm:text-2xl font-semibold rounded-xl border border-border bg-white"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  type="submit"
                  data-testid="patient-verify-otp-btn"
                  disabled={loading || otp.length !== 6}
                  className="w-full h-14 rounded-full text-base bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
                >
                  {loading ? "Verifying…" : "Verify & continue"}
                </Button>

                <button
                  type="button"
                  data-testid="patient-change-phone-btn"
                  onClick={() => {
                    setStep("phone");
                    setOtp("");
                  }}
                  className="w-full text-center text-sm text-muted-foreground hover:text-primary"
                >
                  ← Change phone number
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
